import { ReactElement } from 'react';
import { useAsync } from 'react-use';
import { Chip, Button, Typography, Link } from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import {
  InfoCard,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { stageDeployApiRef } from '../../api/StageDeployApi';
import { DeployState, unknownStatus } from '../../api/types';

function statusChip(state: DeployState): ReactElement {
  switch (state.status) {
    case 'ENABLED':
      return (
        <Chip
          label="Enabled"
          style={{ backgroundColor: '#4caf50', color: 'white' }}
          size="small"
        />
      );
    case 'DISABLED':
      return (
        <Chip
          label="Disabled"
          style={{ backgroundColor: '#9e9e9e', color: 'white' }}
          size="small"
        />
      );
    case 'NOT_IN_ARGO':
      return (
        <Chip
          label="Not Deployed"
          style={{ backgroundColor: '#9e9e9e', color: 'white' }}
          size="small"
        />
      );
    case 'PR_PENDING_ADD':
      return (
        <Link href={state.prUrl} target="_blank" rel="noopener">
          <Chip
            label="Pending PR"
            style={{ backgroundColor: '#ff9800', color: 'white' }}
            size="small"
            clickable
          />
        </Link>
      );
    case 'PR_PENDING_REMOVE':
      return (
        <Link href={state.prUrl} target="_blank" rel="noopener">
          <Chip
            label="Pending Removal"
            style={{ backgroundColor: '#ff9800', color: 'white' }}
            size="small"
            clickable
          />
        </Link>
      );
    default:
      return <Chip label={`Unknown: ${unknownStatus(state)}`} size="small" />;
  }
}

export function DeploymentStatusCard() {
  const { entity } = useEntity();
  const api = useApi(stageDeployApiRef);

  const projectSlug = entity.metadata.annotations?.['github.com/project-slug'];
  const repoName = projectSlug?.split('/').pop();

  const { value: repos, loading, error } = useAsync(() => api.getRepos(), []);

  if (!repoName) {
    return (
      <InfoCard title="Stage Deployment">
        <Typography>Missing annotation: github.com/project-slug</Typography>
      </InfoCard>
    );
  }

  if (loading) {
    return (
      <InfoCard title="Stage Deployment">
        <Progress />
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="Stage Deployment">
        <ResponseErrorPanel error={error} />
      </InfoCard>
    );
  }

  const repo = repos?.find(r => r.repoName === repoName);

  if (!repo) {
    return (
      <InfoCard title="Stage Deployment">
        <Typography>No deployment info found for {repoName}</Typography>
      </InfoCard>
    );
  }

  return (
    <InfoCard title="Stage Deployment">
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {statusChip(repo.state)}
        {repo.canDeploy && repo.state.status === 'NOT_IN_ARGO' && (
          <Button variant="contained" color="primary" size="small">
            Enable for Stage
          </Button>
        )}
        {repo.canDeploy && repo.state.status === 'ENABLED' && (
          <Button variant="outlined" color="secondary" size="small">
            Disable
          </Button>
        )}
      </div>
    </InfoCard>
  );
}
